import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import type { FormInstance } from 'antd'
import { Avatar, Button, Divider, Input, Select, Table, Typography } from 'antd'
import clsx from 'clsx'
import useDebounce from 'hooks/useDebounce'
import useSWR from 'swr'

import UserGroupIcon from 'components/icons/UserGroupIcon'

type UserItem = {
  id: number
  userId: number
  email: string
  lastName: string
  firstName: string
  organizationName?: string
  companyName?: string
  permission: 'view' | 'edit'
  role?: 'VIEW' | 'EDIT'
  organization?: {
    id: number
    name?: string
  }
}

/** API may return organization (object) or organizationName (string) */
function getOrganizationName(user: {
  organization?: { name?: string }
  organizationName?: string
  companyName?: string
}): string | undefined {
  return user.organization?.name ?? user.organizationName ?? user.companyName
}

type Props = {
  form: FormInstance
  fieldName?: string
  initialUsers?: Array<{
    userId: number
    email: string
    permission: string
    lastName?: string
    firstName?: string
    organizationName?: string
  }>
  onChange?: (val: number[]) => void
  disabled?: boolean
}

export default function UserSelector({
  form,
  fieldName = 'users',
  initialUsers,
  onChange,
  disabled = false
}: Props) {
  const [searchString, setSearchString] = useState<string>('')
  const [registeredUsers, setRegisteredUsers] = useState<UserItem[]>([])
  const [inviteError, setInviteError] = useState(false)
  const [permissionInSelect, setPermissionInSelect] = useState<'view' | 'edit'>(
    'view'
  )
  const [filterString, setFilterString] = useState('')
  const [selectedUsersData, setSelectedUsersData] = useState<UserItem[]>([])

  const debouncedSearchString = useDebounce(searchString, 500)
  const { id: buildingId } = useParams()

  const { isValidating: isValidatingSearch, data: searchData } = useSWR(
    debouncedSearchString
      ? {
          url: '/v1/alerts/users',
          params: {
            building_id: buildingId,
            keyword: debouncedSearchString,
            page_size: 500,
            page: 1
          }
        }
      : null,
    {
      onSuccess: () => {
        setInviteError(false)
      },
      onError: () => {
        setInviteError(true)
      }
    }
  )

  // /v1/users returns Collection with edges array
  const userCanBeInvited: UserItem[] =
    searchData?.edges ??
    searchData?.users ??
    (Array.isArray(searchData) ? searchData : [])

  const listUsers = userCanBeInvited.filter(
    (u: { id: number }) => !registeredUsers.some((r) => r.userId === u.id)
  )

  const syncFormUsers = useCallback(
    (users: UserItem[]) => {
      // API payload requires user_ids as an array of numbers: [504, 501]
      const userIds = users.map((u) => u.userId)
      form.setFieldValue(fieldName, userIds)
      form.setFieldValue(`${fieldName}_data`, users)
      if (onChange) {
        onChange(userIds)
      }
    },
    [form, fieldName, onChange]
  )

  const hasInitializedRef = useRef(false)

  useEffect(() => {
    if (initialUsers?.length && !hasInitializedRef.current) {
      hasInitializedRef.current = true
      const mapped: UserItem[] = initialUsers.map((u) => ({
        id: u.userId,
        userId: u.userId,
        email: u.email,
        lastName: u.lastName || u.email.split('@')[0] || '',
        firstName: u.firstName || '',
        organizationName: u.organizationName,
        permission: (u.permission as 'view' | 'edit') || 'view',
        role: (u as any).role,
        organization: (u as any).organization
      }))
      setRegisteredUsers(mapped)
    }
  }, [initialUsers])

  useEffect(() => {
    syncFormUsers(registeredUsers)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registeredUsers])

  const handleAddUser = () => {
    const usersToAdd: UserItem[] = selectedUsersData
      .filter((user) => !registeredUsers.some((r) => r.userId === user.id))
      .map((user) => ({
        id: user.id,
        userId: user.id,
        email: user.email,
        lastName: user.lastName || '',
        firstName: user.firstName || '',
        organizationName: getOrganizationName(user),
        companyName: user.companyName,
        permission: permissionInSelect,
        role: permissionInSelect.toUpperCase() as 'VIEW' | 'EDIT',
        organization: (user as any).organization
      }))
    if (usersToAdd.length === 0) return
    setRegisteredUsers((prev) => [...prev, ...usersToAdd])
    setSearchString('')
    setSelectedUsersData([])
    setInviteError(false)
  }

  const handleRemoveUser = (userId: number) => {
    setRegisteredUsers((prev) => prev.filter((r) => r.userId !== userId))
  }

  const handleChangePermission = (userId: number, perm: 'view' | 'edit') => {
    setRegisteredUsers((prev) =>
      prev.map((u) =>
        u.userId === userId
          ? {
              ...u,
              permission: perm,
              role: perm.toUpperCase() as 'VIEW' | 'EDIT'
            }
          : u
      )
    )
  }

  const filteredRegistered =
    filterString.trim() === ''
      ? registeredUsers
      : registeredUsers.filter(
          (u) =>
            u.email.toLowerCase().includes(filterString.toLowerCase()) ||
            `${u.lastName} ${u.firstName}`
              .toLowerCase()
              .includes(filterString.toLowerCase())
        )

  const columns = [
    {
      title: '名前',
      key: 'name',
      width: 100,
      render: (_: unknown, record: UserItem) => (
        <span className="text-sm text-[#021120] font-medium py-1 block">
          {record.lastName}&nbsp;{record.firstName}
        </span>
      )
    },
    {
      title: '組織名',
      dataIndex: 'organizationName',
      key: 'organizationName',
      width: 120,
      render: (_: unknown, record: UserItem) => (
        <span className="text-sm text-[#021120]">
          {record.organizationName || record.companyName || '-'}
        </span>
      )
    },
    {
      title: 'メールアドレス',
      dataIndex: 'email',
      key: 'email',
      // No width specified, allowing it to expand
      render: (_: unknown, record: UserItem) => (
        <span className="text-sm text-[#021120]">{record.email}</span>
      )
    },
    {
      title: '権限',
      dataIndex: 'permission',
      key: 'permission',
      width: 130,
      render: (_: unknown, record: UserItem) => (
        <Select
          value={record.permission}
          disabled={disabled}
          onChange={(val: 'view' | 'edit') =>
            handleChangePermission(record.userId, val)
          }
          bordered={false}
          className="w-[110px]"
          options={[
            { value: 'view', label: '閲覧者' },
            { value: 'edit', label: '編集者' }
          ]}
        />
      )
    },
    {
      title: '',
      key: 'action',
      width: 36,
      render: (_: unknown, record: UserItem) => (
        <div
          className={clsx(
            'flex justify-center items-center w-5 h-5 rounded-full text-[#3B3C46] text-[14px] leading-none',
            disabled
              ? 'cursor-not-allowed opacity-50'
              : 'cursor-pointer hover:bg-gray-100'
          )}
          onClick={() => !disabled && handleRemoveUser(record.userId)}
          onKeyDown={(e) =>
            e.key === 'Enter' && handleRemoveUser(record.userId)
          }
          role="button"
          tabIndex={0}
          aria-label="削除"
        >
          ×
        </div>
      )
    }
  ]

  return (
    <div
      style={{
        border: '1px solid #CED0D4',
        borderRadius: 8,
        overflow: 'hidden'
      }}
    >
      <style>{`
        .user-selector-table .ant-table-thead > tr > th {
          background-color: #3F63B6 !important;
          color: white !important;
          font-size: 14px;
          height: 40px;
          padding: 8px 16px !important;
        }
        .user-selector-table .ant-table-thead > tr > th::before {
          display: none !important;
        }
        .user-selector-table .ant-table-tbody > tr > td {
          padding: 8px 16px !important;
        }
      `}</style>
      <div className="p-4">
        <div className="flex gap-2 mb-4 items-center">
          <div className="flex-1 relative flex items-center">
            <Select
              mode="multiple"
              disabled={disabled}
              placeholder="招待するユーザーのメールアドレスを入力"
              value={selectedUsersData.map((u) => u.id)}
              filterOption={false}
              autoClearSearchValue
              onSearch={(val: string) => setSearchString(val)}
              onChange={(val: number[]) => {
                const valSet = new Set(val)
                setSelectedUsersData((prev) => {
                  // Keep users that are still selected
                  const retained = prev.filter((u) => valSet.has(u.id))
                  const retainedIds = new Set(retained.map((u) => u.id))
                  // Add newly selected users — look up full data from current search results
                  const added = val
                    .filter((id) => !retainedIds.has(id))
                    .map((id) => userCanBeInvited.find((u) => u.id === id))
                    .filter((u): u is UserItem => !!u)
                  return [...retained, ...added]
                })
                setSearchString('')
              }}
              onFocus={() => setInviteError(false)}
              className={clsx(
                'flex-1 min-h-[38px] [&_.ant-select-selection-overflow]:pr-[110px]',
                {
                  'error-email-select': inviteError
                }
              )}
              notFoundContent={(() => {
                if (isValidatingSearch) return '検索中...'
                if (searchString) return '該当なし'
                return null
              })()}
              suffixIcon={null}
              optionLabelProp="emailLabel"
              loading={isValidatingSearch}
              getPopupContainer={(node) => node.parentElement || document.body}
              dropdownStyle={{ zIndex: 2000 }}
              options={(() => {
                const toOption = (user: UserItem) => ({
                  value: user.id,
                  emailLabel: user.email,
                  label: (
                    <div className="flex gap-3 items-center" key={user.id}>
                      <Avatar
                        className="min-w-[2.5rem] text-black font-light"
                        size="large"
                      >
                        {(user.lastName || '').split('')[0]}
                        {(user.firstName || '').split('')[0]}
                      </Avatar>
                      <div>
                        <div className="text-sm text-[#021120]">
                          {user.lastName}&nbsp;{user.firstName}
                        </div>
                        <div className="text-xs text-[#555]">{user.email}</div>
                      </div>
                    </div>
                  )
                })
                if (isValidatingSearch) return []
                const listIds = new Set(listUsers.map((u: UserItem) => u.id))
                // Include previously-selected users (from older searches) so
                // their tags still display the email label correctly
                const extraSelected = selectedUsersData
                  .filter((u) => !listIds.has(u.id))
                  .map(toOption)
                return [...extraSelected, ...listUsers.map(toOption)]
              })()}
            />
            <div className="absolute right-0 top-0 h-full flex items-center pr-1">
              <Divider type="vertical" className="h-[20px] bg-[#CED0D4] mx-0" />
              <Select
                value={permissionInSelect}
                disabled={disabled}
                onChange={(val: 'view' | 'edit') => setPermissionInSelect(val)}
                bordered={false}
                className="w-[110px]"
                options={[
                  { value: 'view', label: '閲覧者' },
                  { value: 'edit', label: '編集者' }
                ]}
              />
            </div>
          </div>
          <Button
            type="primary"
            disabled={disabled}
            className="h-[38px] w-[110px] bg-[#3F63B6] hover:!bg-[#36539a] border-none font-bold"
            onClick={() => {
              if (selectedUsersData.length > 0) {
                handleAddUser()
              }
            }}
          >
            招待
          </Button>
        </div>

        <Divider className="border-[#CED0D4] my-0 mb-[14px]" />

        <div className="flex gap-[6px] mb-2">
          <UserGroupIcon />
          <Typography className="text-sm leading-7 text-[#021120]">
            メールアドレスで検索することができます。
          </Typography>
        </div>
        <div className="flex gap-2 mb-3">
          <Input
            placeholder="メールアドレスをカンマで区切って入力"
            disabled={disabled}
            className="flex-1 text-sm h-[38px]"
            value={filterString}
            onChange={(e) => setFilterString(e.target.value)}
          />
          <Button
            type="primary"
            disabled={disabled}
            className="h-[38px] w-[110px] bg-[#3F63B6] hover:!bg-[#36539a] border-none font-bold"
          >
            検索
          </Button>
        </div>
      </div>

      <div className="mx-4 mb-4">
        <Table
          columns={columns}
          dataSource={filteredRegistered}
          rowKey="userId"
          pagination={false}
          size="small"
          scroll={{ y: 180 }}
          className="user-selector-table [&_.ant-table-container]:!border-none [&_.ant-table-container]:!border-0"
        />
      </div>
    </div>
  )
}

UserSelector.defaultProps = {
  fieldName: 'users',
  initialUsers: undefined,
  onChange: undefined,
  disabled: false
}
